import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabaseClient';

const { FiBell, FiCalendar, FiTruck, FiCheckCircle, FiAlertTriangle, FiMail, FiSmartphone, FiClock } = FiIcons;

const Reminders = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    fleetSize: '',
    nextInspection: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newSubscriber = {
      id: Date.now(),
      name: formData.name,
      company: formData.company,
      email: formData.email,
      phone: formData.phone,
      fleetSize: formData.fleetSize,
      nextInspection: formData.nextInspection,
      status: 'Active',
      joinedDate: new Date().toLocaleDateString()
    };

    try {
      // 1. Save to Local Storage (Admin Panel Database)
      const existingSubs = JSON.parse(localStorage.getItem('ramirez_subscribers') || '[]');
      // If we are the first to write, we might want to preserve defaults if they haven't been loaded yet,
      // but typically we just append. If the admin panel hasn't been opened yet, this will start the list.
      const updatedSubs = [newSubscriber, ...existingSubs];
      localStorage.setItem('ramirez_subscribers', JSON.stringify(updatedSubs));

      // 2. Save to Supabase (Optional - if connected)
      if (supabase) {
        const { error } = await supabase
          .from('leads')
          .insert([
            {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              company: formData.company,
              type: 'Reminder',
              status: 'New',
              message: `Fleet Size: ${formData.fleetSize}\nNext Inspection: ${formData.nextInspection}`
            }
          ]);
        if (error) {
          console.error("Supabase Error:", error);
        }
      } else {
        // Simulate network delay if no backend
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission Error:', error);
      setIsSubmitted(true); // Show success anyway for demo purposes
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEmailApp = () => {
    const subject = `New Reminder Subscription: ${formData.company || formData.name}`;
    const body = `Name: ${formData.name}%0D%0ACompany: ${formData.company}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0AFleet Size: ${formData.fleetSize}%0D%0ANext Inspection: ${formData.nextInspection}`;
    window.location.href = `mailto:cleanairtruckcheck@gmail.com?subject=${subject}&body=${body}`;
  };

  const benefits = [
    { icon: FiClock, title: "Avoid Downtime", description: "Schedule inspections before they become urgent to keep your trucks on the road." },
    { icon: FiAlertTriangle, title: "Prevent Fines", description: "Missed CARB deadlines can result in significant penalties. We help you stay safe." },
    { icon: FiCalendar, title: "Automated Scheduling", description: "Get notified 30, 15, and 7 days before your compliance certification expires." },
    { icon: FiCheckCircle, title: "Peace of Mind", description: "Focus on your business while we track your fleet's regulatory status." }
  ];

  return (
    <div className="bg-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&w=1920&q=80" alt="Calendar and Truck" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-800/90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block p-4 rounded-full bg-yellow-500/20 mb-6"
          >
            <SafeIcon icon={FiBell} className="text-5xl text-yellow-500" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Never Miss a CARB Compliance Deadline
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Sign up for our free automated reminder service. We'll track your fleet's Clean Truck Check dates and notify you when it's time for inspection.
          </motion.p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form Section */}
            <div>
              <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 overflow-hidden">
                <div className="bg-blue-600 p-6 text-white">
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <SafeIcon icon={FiBell} />
                    Subscribe to Alerts
                  </h3>
                  <p className="text-blue-100 mt-2">Get text & email notifications for your fleet.</p>
                </div>
                <div className="p-8">
                  {!isSubmitted ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-gray-300 font-semibold mb-2">Full Name</label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-400"
                            placeholder="John Doe"
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 font-semibold mb-2">Company Name</label>
                          <input
                            type="text"
                            name="company"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-400"
                            placeholder="JD Trucking"
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-gray-300 font-semibold mb-2">Email Address</label>
                          <div className="relative">
                            <SafeIcon icon={FiMail} className="absolute left-3 top-3.5 text-gray-400" />
                            <input
                              type="email"
                              name="email"
                              required
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-400"
                              placeholder="john@example.com"
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-gray-300 font-semibold mb-2">Phone Number</label>
                          <div className="relative">
                            <SafeIcon icon={FiSmartphone} className="absolute left-3 top-3.5 text-gray-400" />
                            <input
                              type="tel"
                              name="phone"
                              required
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-400"
                              placeholder="(555) 123-4567"
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-gray-300 font-semibold mb-2">Fleet Size</label>
                          <select
                            name="fleetSize"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-white"
                            onChange={handleChange}
                          >
                            <option value="">Select size...</option>
                            <option value="1-5">1-5 Trucks</option>
                            <option value="6-20">6-20 Trucks</option>
                            <option value="20+">20+ Trucks</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-gray-300 font-semibold mb-2">Est. Next Inspection</label>
                          <input
                            type="date"
                            name="nextInspection"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-white"
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full bg-yellow-500 text-gray-900 font-bold py-4 rounded-lg hover:bg-yellow-400 transition-colors shadow-md text-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? 'Processing...' : 'Activate Reminders'}
                      </button>
                      <p className="text-xs text-center text-gray-400 mt-4">
                        By submitting, you agree to receive automated reminders. You can unsubscribe at any time.
                      </p>
                    </form>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <SafeIcon icon={FiCheckCircle} className="text-5xl text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">Registration Complete!</h3>
                      <p className="text-gray-300 mb-6">
                        We have added you to our reminder system. To ensure we have your details immediately, please send confirmation via email below.
                      </p>
                      <button
                        onClick={handleOpenEmailApp}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-bold flex items-center gap-2 mx-auto mb-6"
                      >
                        <SafeIcon icon={FiMail} />
                        Confirm via Email App
                      </button>
                      <p className="text-sm text-gray-500 mb-4">
                        Sent to: <span className="text-gray-300">cleanairtruckcheck@gmail.com</span>
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="text-blue-400 font-semibold hover:underline text-sm"
                      >
                        Register another contact
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="flex flex-col justify-center">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-white mb-4">Why Sign Up?</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Managing a fleet is hard enough without worrying about expiring certifications. Our reminder system is designed specifically for California trucking companies navigating the complex CARB Clean Truck Check requirements.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:bg-gray-750 transition-colors"
                  >
                    <SafeIcon icon={benefit.icon} className="text-3xl text-blue-500 mb-3" />
                    <h4 className="text-xl font-bold text-white mb-2">{benefit.title}</h4>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-10 p-6 bg-yellow-900/20 rounded-xl border border-yellow-700/50">
                <div className="flex items-start gap-4">
                  <SafeIcon icon={FiAlertTriangle} className="text-3xl text-yellow-500 mt-1" />
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">Did You Know?</h4>
                    <p className="text-gray-300 text-sm">
                      Effective 2024, the CARB Clean Truck Check program requires more frequent testing for nearly all diesel trucks operating in California. Don't get caught off guard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reminders;